import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useTranslation } from '../hooks/useTranslation';

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * RiskGauge component displays a semi-circle gauge for risk score
 */
export default function RiskGauge({ score = 0, min = 0, max = 100, title = 'Risk Score' }) {
  const { t } = useTranslation();
  
  // Normalize score to 0-100
  const normalizedScore = Math.max(min, Math.min(max, score));
  const percentage = ((normalizedScore - min) / (max - min)) * 100;

  // Determine color and label based on score
  let color, bgColor, label, status;
  if (normalizedScore < 30) {
    color = '#10B981'; // green
    bgColor = 'rgba(16, 185, 129, 0.1)';
    label = t('risk.low');
    status = 'SAFE';
  } else if (normalizedScore < 70) {
    color = '#F59E0B'; // yellow/orange
    bgColor = 'rgba(245, 158, 11, 0.1)';
    label = t('risk.moderate');
    status = t('risk.caution');
  } else {
    color = '#EF4444'; // red
    bgColor = 'rgba(239, 68, 68, 0.1)';
    label = t('risk.high');
    status = 'DANGER';
  }

  // Chart data - semi-circle gauge
  const data = {
    labels: ['Risk', 'Safe'],
    datasets: [
      {
        data: [percentage, 100 - percentage],
        backgroundColor: [color, '#E5E7EB'],
        borderWidth: 0,
        circumference: 180,
        rotation: 270
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    },
    cutout: '75%'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 text-center">
        {title}
      </h2>
      
      <div className="relative w-full max-w-xs mx-auto">
        {/* Gauge chart */}
        <Doughnut data={data} options={options} />
        
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ top: '40%' }}>
          <div className="text-5xl font-bold" style={{ color }}>
            {normalizedScore}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            / {max}
          </div>
        </div>
      </div>

      {/* Status badge */}
      <div className="mt-6 text-center">
        <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold text-white`} style={{ backgroundColor: color }}>
          {status}
        </div>
        <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm">
          {label}
        </p>
      </div>

      {/* Risk levels legend */}
      <div className="mt-6 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-success mr-2"></div>
            <span className="text-gray-600 dark:text-gray-400">{t('risk.low')}</span>
          </div>
          <span className="text-gray-500 text-xs">0-29</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-warning mr-2"></div>
            <span className="text-gray-600 dark:text-gray-400">{t('risk.moderate')}</span>
          </div>
          <span className="text-gray-500 text-xs">30-69</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-danger mr-2"></div>
            <span className="text-gray-600 dark:text-gray-400">{t('risk.high')}</span>
          </div>
          <span className="text-gray-500 text-xs">70-100</span>
        </div>
      </div>

      {/* Recommendation */}
      <div className={`mt-6 p-3 rounded-lg ${normalizedScore < 30 ? 'bg-success/10' : normalizedScore < 70 ? 'bg-warning/10' : 'bg-danger/10'}`}>
        <p className="text-xs text-gray-700 dark:text-gray-300">
          {normalizedScore < 30 && '✓ Risk levels are within acceptable range. Continue monitoring.'}
          {normalizedScore >= 30 && normalizedScore < 70 && t('risk.warning')}
          {normalizedScore >= 70 && '🚨 Critical risk level! Immediate action recommended. Consider reducing exposure.'}
        </p>
      </div>
    </div>
  );
}

